/**
 * Stratolaunch cookie consent
 * ---------------------------
 * orestbida CookieConsent v3 (https://cookieconsent.orestbida.com/)
 * Self-hosted, bundled via laravel-mix -> /dist/js/consent.js
 * Loaded with `defer` from <head> (templates/base.twig). Trackers are gated
 * (type="text/plain") and only activated by run() below, so Google Consent Mode is
 * still set to "denied" before gtag can run. (Site is served statically by Blitz →
 * client-side gating.)
 *
 * Trackers:
 *   analytics -> GA4 (G-ECE43DNF71, _partials/tracking.twig) + Vimeo (dnt-gated)
 *   necessary -> always on (Freeform CSRF, session, the consent cookie itself)
 *
 * GA4 is gated by tagging its <script> type="text/plain" data-category="analytics"
 * in tracking.twig. Vimeo players read consent in the ui/js/modules/*video* files
 * (dnt: true unless analytics accepted).
 */

import * as CookieConsent from 'vanilla-cookieconsent';

// Expose globally so the footer "Cookie Settings" link can reopen the panel:
// onclick="CookieConsent.showPreferences()"
window.CookieConsent = CookieConsent;

/* Google Consent Mode v2 — default denied BEFORE gtag loads (this is the first script). */
window.dataLayer = window.dataLayer || [];
function gtag() { window.dataLayer.push(arguments); }
window.gtag = window.gtag || gtag;

gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
});

function syncGoogleConsent() {
    gtag('consent', 'update', {
        analytics_storage: CookieConsent.acceptedCategory('analytics') ? 'granted' : 'denied',
    });
}

// false hides the banner and forces analytics on (set in base.twig). Defaults on if missing.
const consentEnabled = window.COOKIE_CONSENT_ENABLED !== false;

const ccConfig = {
    guiOptions: {
        consentModal: { layout: 'box wide', position: 'bottom left' },
        preferencesModal: { layout: 'box' },
    },

    categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {
            autoClear: { cookies: [{ name: /^_ga/ }, { name: '_gid' }] },
        },
    },

    autoShow: consentEnabled,

    onFirstConsent: syncGoogleConsent,
    onConsent: syncGoogleConsent,
    // Changing choices later reloads so trackers re-init cleanly; disabled mode re-syncs
    // instead, so the forced accept below can't loop. (onChange skips the first choice.)
    onChange: consentEnabled ? () => window.location.reload() : syncGoogleConsent,

    language: {
        default: 'en',
        translations: {
            en: {
                consentModal: {
                    title: 'We use cookies',
                    description:
                        'We use cookies to run the site and understand how it’s used. ' +
                        'You can accept all, reject non-essential, or choose your preferences.',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    showPreferencesBtn: 'Manage preferences',
                },
                preferencesModal: {
                    title: 'Cookie preferences',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    savePreferencesBtn: 'Save preferences',
                    closeIconLabel: 'Close',
                    sections: [
                        {
                            title: 'Strictly necessary',
                            description: 'Required for the site to function. Always on.',
                            linkedCategory: 'necessary',
                            cookieTable: {
                                headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
                                body: [
                                    { name: 'CraftSessionId', service: 'Stratolaunch', desc: 'Maintains your session as you browse', exp: 'Session' },
                                    { name: 'CRAFT_CSRF_TOKEN', service: 'Stratolaunch', desc: 'Protects forms against cross-site request forgery', exp: 'Session' },
                                    { name: 'cc_cookie', service: 'Stratolaunch', desc: 'Stores your cookie consent choices', exp: '6 months' },
                                    { name: 'freeform_*', service: 'Stratolaunch', desc: 'Spam protection and submission handling on forms', exp: 'Session' },
                                    { name: '__cf_bm', service: 'Cloudflare (Vimeo)', desc: 'Bot protection for embedded video', exp: '30 minutes' },
                                ],
                            },
                        },
                        {
                            title: 'Analytics',
                            description: 'Google Analytics 4 and Vimeo video stats — help us understand site usage and which videos people watch.',
                            linkedCategory: 'analytics',
                            cookieTable: {
                                headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
                                body: [
                                    { name: '_ga', service: 'Google Analytics', desc: 'Distinguishes unique visitors', exp: '2 years' },
                                    { name: '_ga_ECE43DNF71', service: 'Google Analytics', desc: 'GA4 session state', exp: '2 years' },
                                    { name: '_gid', service: 'Google Analytics', desc: 'Distinguishes unique visitors', exp: '24 hours' },
                                    { name: 'vuid', service: 'Vimeo', desc: 'Counts video views (only on play, with consent)', exp: '2 years' },
                                ],
                            },
                        },
                        {
                            title: 'More information',
                            description: 'See our <a href="/privacy-policy">Privacy Policy</a> for details.',
                        },
                    ],
                },
            },
        },
    },
};

// run() appends the banner to document.body; guard for the case this is ever loaded
// synchronously from <head> again.
function startConsent() {
    CookieConsent.run(ccConfig);
    // No banner shown, so grant analytics ourselves — activates the gated GA4/Vimeo trackers.
    if (!consentEnabled) {
        CookieConsent.acceptCategory('all');
    }
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startConsent);
} else {
    startConsent();
}
